import { useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { Alert, Button, Stack, Tooltip } from '@mui/material';

import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import LoadingPanel from 'src/pages/admin/components/LoadingPanel';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionButton } from 'src/routes/components/permission-button';
import { PermissionGuard } from 'src/routes/components/permission-guard';

import { ContactDialog } from '../vaults/ContactDialog';
import { useVaultContact } from '../vaults/useVaultContact';
import { ConfirmRequestDialog } from './ConfirmRequestDialog';
import {
  OFFICIAL_VAULTS_NS as NS,
  OFFICIAL_VAULTS_PERMISSION_RESOURCE as RESOURCE,
} from './constants';
import { DetailsDrawer } from './DetailsDrawer';
import { FaucetDialog } from './FaucetDialog';
import { ApproveDialog } from './ApproveDialog';
import { draftFromRequest, stakeNotice } from './helpers';
import { faucetDefaultAmount, faucetDefaultUserId } from './stakeAmounts';
import { RequestDialog } from './RequestDialog';
import { RedriveDialog } from './RedriveDialog';
import { RejectDialog } from './RejectDialog';
import { RequestsTable } from './RequestsTable';
import {
  useOfficialVaultForm,
  useOfficialVaultRequests,
  useApproveForm,
  useFaucetBank,
  useFaucetForm,
  useRedriveForm,
  useRejectForm,
  useRequestBounds,
} from './useOfficialVaults';

const OfficialVaultsList = () => {
  const translate = useTranslate();
  const { requests, loading, loadError, refetch } = useOfficialVaultRequests();
  const bounds = useRequestBounds();
  // Every listed ID, for a retry's fresh one. A typed name only moves off FAILED IDs, which C-08 keeps for good: an
  // ID a live request holds stays, so resubmitting after a timeout stays idempotent and a clash reads as the
  // engine's own conflict rather than as a silent -2 (review of XBIT-4321).
  const allVaultIds = useMemo(() => new Set(requests.map((r) => r.vaultId)), [requests]);
  const failedVaultIds = useMemo(
    () => new Set(requests.filter((r) => r.status === 'FAILED').map((r) => r.vaultId)),
    [requests]
  );
  const form = useOfficialVaultForm({
    bounds: bounds.bounds,
    onSettled: refetch,
    refetchBounds: bounds.refetch,
    takenVaultIds: failedVaultIds,
  });

  const approval = useApproveForm({ onSettled: refetch });
  // XBIT-4401: the contact edit, the same dialog as Vault Management's; the list is read again after it.
  const contact = useVaultContact({ onSettled: refetch });
  const rejection = useRejectForm({ onSettled: refetch });
  // XBIT-4240: resume a FAILED request from the step that failed.
  const redrive = useRedriveForm({ onSettled: refetch });
  const bank = useFaucetBank();
  const faucet = useFaucetForm({ bank, onSettled: refetch });
  const byId = (id: string | null) => requests.find((r) => r.vaultId === id);
  // By id, so a refetch shows the drawer's request as it is now.
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const details = requests.find((r) => r.vaultId === detailsId) ?? null;

  const requestButton = (
    <Button
      variant="contained"
      startIcon={<Iconify icon="mingcute:add-line" />}
      onClick={() => form.openDialog()}
      disabled={!bounds.bounds}
    >
      {translate(`${NS}.request`)}
    </Button>
  );

  return (
    <PermissionGuard resource={RESOURCE} action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate(`${NS}.title`)}
          links={[
            { name: translate('resource.vault'), href: '#' },
            { name: translate(`${NS}.title`) },
          ]}
          action={
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Iconify icon="solar:refresh-bold" />}
                onClick={() => {
                  refetch();
                  bounds.refetch();
                }}
                disabled={loading}
              >
                {translate(`${NS}.refresh`)}
              </Button>
              <PermissionButton resource={RESOURCE} action="create">
                {bounds.bounds ? (
                  requestButton
                ) : (
                  // A disabled button swallows hover; the span carries the tooltip.
                  <Tooltip
                    title={translate(
                      `${NS}.${bounds.forbidden ? 'boundsForbidden' : 'boundsUnavailable'}`
                    )}
                  >
                    <span style={{ display: 'inline-flex' }}>{requestButton}</span>
                  </Tooltip>
                )}
              </PermissionButton>
            </Stack>
          }
        />

        <Stack spacing={3} sx={{ mt: 3 }}>
          <Alert severity="info">{translate(`${NS}.asyncHint`)}</Alert>
          {loadError && <Alert severity="error">{loadError}</Alert>}
          {/* Only an operator who can request needs to know the bounds failed to load. */}
          <PermissionButton resource={RESOURCE} action="create">
            {bounds.loadError && <Alert severity="warning">{bounds.loadError}</Alert>}
          </PermissionButton>
          <LoadingPanel isLoading={loading}>
            <RequestsTable
              requests={requests}
              onViewDetails={setDetailsId}
              onEditContact={(r) => contact.open({ vaultId: r.vaultId, name: r.name })}
            />
          </LoadingPanel>
        </Stack>

        <RequestDialog form={form} bounds={bounds.bounds} />
        <ConfirmRequestDialog form={form} />
        <DetailsDrawer
          request={details}
          onClose={() => setDetailsId(null)}
          onApprove={(request) => {
            setDetailsId(null);
            approval.openDialog(request.vaultId);
          }}
          onReject={(request) => {
            setDetailsId(null);
            rejection.openDialog(
              request.vaultId,
              request.review?.outcome === 'REJECTED' ? (request.review.reason ?? '') : ''
            );
          }}
          onRedrive={(request) => {
            setDetailsId(null);
            redrive.openDialog(request.vaultId);
          }}
          onRetry={(request) => {
            setDetailsId(null);
            form.openDialog(draftFromRequest(request, allVaultIds));
          }}
          bank={bank}
          onFaucet={(vaultId) => {
            setDetailsId(null);
            // XBIT-4400: suggest what the stake needs (stake + 1 before ACTIVATED), capped at the faucet ceiling.
            const request = byId(vaultId);
            faucet.openDialog(
              vaultId,
              request ? faucetDefaultAmount(request, bank.offered ? bank.maxAmount : null) : null,
              // XBIT-4469 (D2): and, while the stake is owed, the manager it must be credited to.
              request ? faucetDefaultUserId(request) : ''
            );
          }}
        />
        <ApproveDialog
          form={approval}
          stake={stakeNotice(byId(approval.vaultId))}
          request={byId(approval.vaultId)}
        />
        <RejectDialog
          form={rejection}
          stakeReceived={byId(rejection.vaultId)?.stake?.received ?? null}
          request={byId(rejection.vaultId)}
          retryAfter={rejection.retryAfter}
        />
        <RedriveDialog form={redrive} request={byId(redrive.vaultId)} />
        {bank.offered && <FaucetDialog form={faucet} bank={bank} />}
        <ContactDialog form={contact} />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default OfficialVaultsList;
